import { backend, isLocalMode } from '../data/backend.js';
import { toast } from './toast.js';

/**
 * Konto-Dialog. Mit PocketBase meldet man sich mit Name und Passwort an, Konten legt
 * nur der Administrator an. Ohne Server bleibt es beim lokalen Profil: das trennt
 * Favoriten pro Name, aber nur auf diesem Gerät.
 */
export function createAccount({ button, onChange }) {
  const el = (id) => document.getElementById(id);
  const dialog = el('account-dialog');
  const form = el('account-form');
  const title = el('account-title');
  const note = el('account-note');
  const idLabel = el('account-id-label');
  const idInput = el('account-id');
  const idField = idInput.closest('.field');
  const pwField = el('account-pw-field');
  const pwLabel = el('account-pw-label');
  const pwInput = el('account-pw');
  const pw2Field = el('account-pw2-field');
  const pw2Input = el('account-pw2');
  const errorEl = el('account-error');
  const cancelBtn = el('account-cancel');
  const absenden = form.querySelector('button[type="submit"]');

  let profile = null;
  let ansicht = 'anmelden'; // oder 'konto', 'passwort'

  // Der zweite Weg im Dialog: vom Konto zum Passwort.
  const zweiterWeg = document.createElement('button');
  zweiterWeg.type = 'button';
  zweiterWeg.className = 'linkbtn';
  zweiterWeg.style.marginRight = 'auto';
  zweiterWeg.textContent = 'Passwort ändern';
  zweiterWeg.addEventListener('click', () => zeigen('passwort'));
  document.querySelector('.dialog__actions')?.prepend(zweiterWeg);

  function paintButton() {
    const label = profile ? profile.name : (isLocalMode ? 'Profil' : 'Anmelden');
    const text = button.querySelector('.navbtn__text');
    if (text) text.textContent = label;
    else button.textContent = label;
    button.title = profile ? `Angemeldet als ${profile.name}` : (isLocalMode ? 'Lokales Profil anlegen' : 'Anmelden');
    button.setAttribute('aria-label', button.title);
  }

  /** Der Dialog hat drei Gesichter; jedes sagt, welche Felder gelten. */
  function zeigen(welche) {
    ansicht = welche;
    errorEl.hidden = true;
    idInput.value = '';
    pwInput.value = '';
    pw2Input.value = '';
    const anmelden = welche === 'anmelden';
    const passwort = welche === 'passwort';

    idField.hidden = !anmelden;
    idInput.required = anmelden;
    pwField.hidden = !(passwort || (anmelden && !isLocalMode));
    pwInput.required = !pwField.hidden;
    pw2Field.hidden = !passwort;
    pw2Input.required = passwort;
    zweiterWeg.hidden = !(welche === 'konto' && profile?.server);

    if (anmelden) {
      title.textContent = isLocalMode ? 'Lokales Profil' : 'Anmelden';
      note.textContent = isLocalMode
        ? 'Ein Name trennt Favoriten und Touren, wenn mehrere dieses Gerät nutzen. Alles bleibt in diesem Browser.'
        : 'Favoriten, Touren und Einstellungen liegen dann im Konto, auf jedem Gerät dieselben. Was bisher im Browser lag, wird übernommen.';
      idLabel.textContent = isLocalMode ? 'Profilname' : 'Name';
      pwLabel.textContent = 'Passwort';
      absenden.textContent = 'Weiter';
    } else if (welche === 'konto') {
      title.textContent = profile.name;
      note.textContent = profile.local
        ? 'Lokales Profil: Favoriten, Touren und Einstellungen liegen nur in diesem Browser.'
        : 'Favoriten, Touren und Einstellungen liegen im Konto, und nur dieses Konto sieht sie.';
      absenden.textContent = 'Abmelden';
    } else {
      title.textContent = 'Passwort ändern';
      note.textContent = 'Mindestens acht Zeichen.';
      pwLabel.textContent = 'Aktuelles Passwort';
      absenden.textContent = 'Ändern';
    }
    if (!dialog.open) dialog.showModal();
    (anmelden ? idInput : (passwort ? pwInput : absenden)).focus();
  }

  async function restore() {
    try {
      profile = await backend.auth.getSession();
    } catch {
      profile = null;
    }
    paintButton();
    onChange?.(profile);
    return profile;
  }

  async function logout() {
    await backend.auth.logout();
    profile = null;
    paintButton();
    onChange?.(profile);
    toast('Abgemeldet.');
  }

  /** Was noch aus der Zeit vor dem Konto im Browser liegt. */
  async function bestandUebernehmen() {
    let z = null;
    try {
      z = await backend.uebernehmen(profile);
    } catch {
      toast('Nicht alles liess sich übernehmen. Beim nächsten Anmelden geht es weiter.', 5000);
      return;
    }
    if (!z || !(z.favoriten + z.touren)) {
      toast(`Angemeldet als ${profile.name}.`);
      return;
    }
    const teile = [
      z.favoriten ? `${z.favoriten} ${z.favoriten === 1 ? 'Favorit' : 'Favoriten'}` : null,
      z.touren ? `${z.touren} ${z.touren === 1 ? 'Tour' : 'Touren'}` : null
    ].filter(Boolean).join(' und ');
    toast(`Angemeldet als ${profile.name}. ${teile} ins Konto übernommen.`, 5000);
  }

  button.addEventListener('click', () => zeigen(profile ? 'konto' : 'anmelden'));
  cancelBtn.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.hidden = true;
    absenden.disabled = true;
    try {
      if (ansicht === 'konto') {
        dialog.close();
        await logout();
      } else if (ansicht === 'passwort') {
        await backend.auth.passwortAendern(profile, pwInput.value, pw2Input.value);
        dialog.close();
        toast('Passwort geändert.');
      } else {
        profile = await backend.auth.login(idInput.value, pwInput.value);
        if (!profile) throw new Error('Anmeldung fehlgeschlagen.');
        dialog.close();
        paintButton();
        await bestandUebernehmen();
        onChange?.(profile);
      }
    } catch (err) {
      errorEl.textContent = err.message || 'Hat nicht geklappt.';
      errorEl.hidden = false;
    } finally {
      absenden.disabled = false;
    }
  });

  return { restore, getProfile: () => profile, logout };
}
